import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Caution, Dialog, Field, SECONDARY_BUTTON } from '@/features/inventory/registerUi';
import {
  classifyInterlockError,
  useSetInterlock,
  type InterlockType,
  type SetInterlockResult,
} from './useInterlock';

/**
 * SLICE 14e — RECORD A DEVICE'S ENGINE INTERLOCK.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THE ONE CHOICE ON THIS SCREEN SWITCHES OFF TWO SAFETY GUARDS.
 * ─────────────────────────────────────────────────────────────────────────────
 * Choosing `starter` tells the command guard to skip its speed and freshness
 * checks for this device. That is correct for a relay wired across the starter
 * (it cannot stall a running engine) and catastrophic for one wired to fuel (it
 * can). No code can tell the two apart — only a human looking at the wiring —
 * so this dialog is the last thing between an operator and that mistake, and it
 * makes the consequence unmissable:
 *
 *   - a plain statement of what `starter` does (the guards go off);
 *   - a MANDATORY inspection note, min 10 chars client-side, the server's 422 as
 *     the belt to that suspender — the note is the record of who looked and what
 *     they saw;
 *   - an explicit acknowledgement checkbox naming the physical fact being
 *     attested (this relay opens the STARTER circuit, not fuel or ignition).
 *
 * 🔴 THE SAFE DIRECTIONS CARRY NO CEREMONY. Clearing to "not recorded", and
 * choosing ignition or fuel, RESTORE the full guard, so they need no note and no
 * checkbox and submit in one click. Making the safe correction as easy as
 * possible is the point: a wrong `starter` left in place because fixing it was
 * tedious is the exact failure this asymmetry prevents.
 *
 * The chrome matches `ClearDevicePickerDialog` / `WithdrawClearanceDialog`. It
 * assumes its host gated it to a super-admin (the write route is
 * `requireSuperAdmin`); it renders no role check of its own.
 */

const NOTE =
  'w-full min-h-[5rem] rounded-lg border border-raad-border bg-raad-input px-3 py-2 text-[13.5px] text-raad-text outline-none focus:border-raad-primary';

/** The radio value for "not recorded" — `null` cannot be a form value, so it
 *  travels as this sentinel and is mapped back at submit. */
const NONE = 'none';
type Choice = InterlockType | typeof NONE;

const OPTIONS: Choice[] = ['starter', 'ignition', 'fuel', NONE];

export function SetInterlockDialog({
  device,
  current,
  onClose,
}: {
  device: { deviceId: string; imei: string };
  /** The device's current interlock, so the dialog opens on it. */
  current: InterlockType | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const set = useSetInterlock();
  const [choice, setChoice] = useState<Choice>(current ?? NONE);
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<SetInterlockResult | null>(null);

  const isStarter = choice === 'starter';
  // starter demands an inspection note (min 10 after trim) AND the explicit
  // acknowledgement. Every other choice is the safe direction and is always
  // ready — never harder than the dangerous one.
  const ready = isStarter ? note.trim().length >= 10 && confirmed : true;

  async function submit() {
    if (!ready) return;
    setError(null);
    const interlockType: InterlockType | null = choice === NONE ? null : choice;
    try {
      const result = await set.mutateAsync({
        deviceId: device.deviceId,
        interlockType,
        note: isStarter ? note.trim() : undefined,
      });
      setDone(result);
    } catch (e) {
      setError(t(`interlock.error.${classifyInterlockError(e)}`));
    }
  }

  return (
    <Dialog title={t('interlock.dialog.title', { imei: device.imei })} onClose={onClose} wide>
      {done ? (
        <div className="grid gap-3">
          <p
            className={[
              'rounded-lg border p-3 text-[12.5px] text-raad-text',
              done.interlockType === 'starter'
                ? 'border-raad-warn/40 bg-raad-warn/10'
                : 'border-raad-border bg-raad-bg/40',
            ].join(' ')}
          >
            {done.interlockType === 'starter'
              ? t('interlock.dialog.doneStarter', { imei: device.imei })
              : t('interlock.dialog.done', { imei: device.imei })}
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary"
            >
              {t('interlock.dialog.doneButton')}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-3">
          <p className="text-[13px]">{t('interlock.dialog.intro')}</p>

          <fieldset className="grid gap-2">
            <legend className="mb-1 text-[12px] font-medium text-raad-muted">
              {t('interlock.dialog.typeLabel')}
            </legend>
            {OPTIONS.map((opt) => (
              <label key={opt} className="flex items-start gap-2.5 text-[13px]">
                <input
                  type="radio"
                  name="interlock-type"
                  className="mt-0.5"
                  checked={choice === opt}
                  onChange={() => {
                    setChoice(opt);
                    setError(null);
                    // Leaving starter drops its ceremony so a later re-open on a
                    // safe choice is not silently gated by a stale checkbox.
                    if (opt !== 'starter') setConfirmed(false);
                  }}
                />
                <span>
                  {t(`interlock.dialog.opt.${opt}`)}
                </span>
              </label>
            ))}
          </fieldset>

          {isStarter ? (
            <>
              <Caution>{t('interlock.dialog.starterConsequence')}</Caution>
              <Field
                label={t('interlock.dialog.noteLabel')}
                hint={t('interlock.dialog.noteHint')}
              >
                <textarea
                  className={NOTE}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                />
              </Field>
              <label className="flex items-start gap-2.5 text-[12.5px] text-raad-text">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                <span>{t('interlock.dialog.confirmLabel')}</span>
              </label>
            </>
          ) : choice === NONE ? (
            <p className="text-[12.5px] text-raad-muted">{t('interlock.dialog.safeIntro')}</p>
          ) : null}

          {error ? (
            <p
              role="alert"
              className="rounded-lg border border-raad-error/40 bg-raad-error/10 p-3 text-[12.5px] text-raad-text"
            >
              {error}
            </p>
          ) : null}

          <div className="mt-2 flex items-center justify-end gap-3">
            <button type="button" className={SECONDARY_BUTTON} onClick={onClose}>
              {t('inventory.close')}
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!ready || set.isPending}
              className={[
                'min-h-10 rounded-lg px-4 text-[13px] font-semibold disabled:opacity-40',
                isStarter
                  ? 'bg-raad-warn text-raad-onprimary'
                  : 'bg-raad-primary text-raad-onprimary',
              ].join(' ')}
            >
              {set.isPending
                ? t('interlock.dialog.working')
                : isStarter
                  ? t('interlock.dialog.submitStarter')
                  : t('interlock.dialog.submit')}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
